/**
 * The loan-loss bridge in words (04.2, ADR-0057): what fell, the loss the bank recognised this year (the provision) and how much of it was charged off this year, from the year's
 * reserve roll-forward; what is left waits in the pipeline. Pure; every figure is a report or outlook field.
 */
import type { RoundReport } from "@bsim/contracts";
import type { MarketOutlook } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { formatUsdM } from "../format";

/** Under this fall a driver has not moved enough to name. */
const NAMED_FALL_RATE = 0.005;

/** The commercial property index's worst fall since the start, from the realised series ("Commercial property prices fell 7%"); null when it has not fallen. */
export function propertyFall(outlook: MarketOutlook | undefined): string | null {
  const series = outlook?.series.find((s) => s.id === "creIndex");
  const start = series?.realised[0]?.value;
  if (series === undefined || start === undefined || start <= 0) return null;
  const fall = 1 - Math.min(...series.realised.map((p) => p.value)) / start;
  return fall < NAMED_FALL_RATE ? null : `${series.label} fell ${formatMetric(fall, "rate", { precision: 0 })}`;
}

export interface LossBridge { readonly recognisedUsd: number; readonly chargedOffUsd: number }

/** The year's recognised loss (provision) and charge-offs from the reserve's roll-forward; null when the report carries none or nothing moved. */
export function lossBridge(report: RoundReport): LossBridge | null {
  const acl = report.attribution.rollForwards.find((r) => r.subject === "acl");
  const [provision, chargeOff] = [acl?.flowsUsd.provision ?? 0, acl?.flowsUsd.chargeOff ?? 0];
  return acl === undefined || (provision === 0 && chargeOff === 0) ? null : { recognisedUsd: Math.abs(provision), chargedOffUsd: Math.abs(chargeOff) };
}

/** "Commercial property prices fell 7%: losses recognised $38.0M, of which $12.0M charged off this year; $26.0M waits to be charged off." */
export function lossBridgeNote(report: RoundReport, outlook: MarketOutlook | undefined): string | null {
  const bridge = lossBridge(report);
  if (bridge === null) return null;
  const { recognisedUsd: recognised, chargedOffUsd: charged } = bridge;
  const fall = propertyFall(outlook);
  const lead = fall === null ? "Losses recognized" : `${fall}: losses recognized`;
  return charged <= recognised
    ? `${lead} ${formatUsdM(recognised)}, of which ${formatUsdM(charged)} charged off this year${recognised > charged ? `; ${formatUsdM(recognised - charged)} waits to be charged off` : ""}.`
    : `${lead} ${formatUsdM(recognised)} this year; ${formatUsdM(charged)} was charged off, the extra paid from reserves booked earlier.`;
}
